import React from 'react';
import { useUserStore } from '../../stores/userStore';
import { useTheme } from '../../hooks/useTheme';
import { useProgressDisplayStore } from '../../stores/progressDisplayStore';
import { useXpReward } from '../../hooks/useXpReward';
import { XpChip } from './XpChip';

const clamp = (n: number) => Math.max(0, Math.min(100, isNaN(n) ? 0 : n));

/**
 * Desktop top-right avatar — the trigger for the global notification
 * center (HeaderNotifications overlays the unread badge; the level badge
 * moved off the avatar — the rail carries persistent progress). When the
 * 'header' surface is enabled, an XP gain overlays a TRANSIENT progress
 * ring plus the "+N XP" chip.
 */
export function HeaderAvatarReward({ onClick }: { onClick?: () => void }) {
  const { name, initials, avatarUrl } = useUserStore();
  const { primaryColorCSS: primaryColor, isMetanoia } = useTheme();
  const enabled = useProgressDisplayStore((s) => s.desktopSurfaces.includes('header'));
  const reward = useXpReward();

  const size = 32;          // matches the original w-8 h-8 avatar
  const strokeWidth = 3;
  /**
   * ⚠️⚠️ THE RING HUGS THE AVATAR — the ring box is derived, never a round
   * number. Its inner edge is `ringSize/2 - strokeWidth`, and the avatar's edge
   * is `size/2`; they coincide only when `ringSize === size + strokeWidth * 2`.
   * `size + 8` left a 1px gap, and unlike the mobile ring this one draws NO
   * background track, so that gap was empty space and the arc read as floating
   * beside the avatar instead of around it.
   */
  const ringSize = size + strokeWidth * 2;
  const radius = (ringSize - strokeWidth) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (clamp(reward.userPct) / 100) * circ;
  const showRing = enabled && reward.active;

  return (
    <button
      onClick={onClick}
      className="relative group hidden lg:block"
      title="Notifications"
      style={{ width: size, height: size }}
    >
      {showRing && (
        <svg
          width={ringSize}
          height={ringSize}
          className="pointer-events-none absolute -rotate-90"
          style={{ left: -strokeWidth, top: -strokeWidth, opacity: reward.active ? 1 : 0, transition: 'opacity 400ms ease-out' }}
          aria-hidden
        >
          {isMetanoia && (
            <defs>
              <linearGradient id="metanoia-grad-deskhdr" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3CC2DD" />
                <stop offset="50%" stopColor="#A739E2" />
                <stop offset="100%" stopColor="#F58C56" />
              </linearGradient>
            </defs>
          )}
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={circ}
            strokeDashoffset={offset}
            strokeLinecap="round"
            stroke={isMetanoia ? 'url(#metanoia-grad-deskhdr)' : primaryColor}
            className="drop-shadow-sm"
          />
        </svg>
      )}

      {/* ⚠️⚠️ The gradient ring goes on a WRAPPER, never on the <img>:
          `accent-gradient-border-hover` draws via `::before`, and a replaced
          element (img) has no pseudo-elements to draw on.
          ⚠️ The outer span keeps its original `inset-0 block` — the avatar's
          position is load-bearing against the ring SVG behind it, so the ring is
          added as an extra layer rather than by resizing what was there.
          ⚠️ The utility is inert off the Metanoia theme, so other themes keep
          the flat `ring-primary`; that flat cyan is only ONE stop of the
          gradient the progress ring above it is already drawing. */}
      <span className="absolute inset-0 block">
        <span className={`block h-8 w-8 rounded-full ${
          isMetanoia ? 'accent-gradient-border-hover' : ''
        }`}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className={`h-8 w-8 rounded-full object-cover ring-2 ring-transparent transition-all ${
                isMetanoia ? '' : 'group-hover:ring-primary'
              }`}
            />
          ) : (
            <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium ring-2 ring-transparent transition-all ${
              isMetanoia ? '' : 'group-hover:ring-primary'
            }`}>
              {initials}
            </span>
          )}
        </span>
      </span>

      {enabled && (
        <XpChip
          amount={reward.amount}
          token={reward.token}
          color={reward.domainColor}
          className="right-0 top-0 -translate-y-4"
        />
      )}
    </button>
  );
}
